import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  getUserTeams,
  getInvoicesForReport,
  formatCurrency,
  formatDate,
  getCurrentFactorValue,
} from "../services/firebaseService";
import { DateRange, Invoice, Team } from "../types";
import "./FaturamentoRealReport.css";

interface RealLineData {
  data: string;
  equipe: string;
  planilhaId: string;
  codigo: string;
  servico: string;
  qtd: number;
  preco: number;
  und: string;
  fator: number; // numeric for calculations
  fatorDisplay: string; // preserve DB precision for display/CSV
  totalParcial: number;
}

const FaturamentoRealReport: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<
    "data" | "codigo" | "equipe" | "total"
  >("data");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: "", endDate: "" });

  const setInitialDateRange = () => {
    const currentDay = new Date().getDate();
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);

    let startDate: string, endDate: string;
    if (currentDay < 26) {
      startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-25`;
    } else {
      startDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}-25`;
    }
    setDateRange({ startDate, endDate });
  };

  const loadUserTeams = async () => {
    if (!user) return;
    try {
      const userTeams = await getUserTeams(user.uid);
      setTeams(userTeams);
      setSelectedTeams([]);
    } catch (err) {
      console.error("Error loading teams:", err);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDateRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadReportData = async () => {
    if (selectedTeams.length === 0 || !dateRange.startDate || !dateRange.endDate) {
      alert("Selecione pelo menos uma equipe e defina o período.");
      return;
    }
    setLoading(true);
    try {
      const allInvoices: Invoice[] = [];
      for (const teamId of selectedTeams) {
        const teamInvoices = await getInvoicesForReport(teamId, dateRange.startDate, dateRange.endDate);
        allInvoices.push(...teamInvoices);
      }
      setInvoices(allInvoices);
    } catch (error) {
      console.error("Error loading report data:", error);
      alert("Erro ao carregar dados do relatório.");
    } finally {
      setLoading(false);
    }
  };

  const lineItems = useMemo<RealLineData[]>(() => {
    const factorDefault = getCurrentFactorValue();
    const items: RealLineData[] = [];
    invoices.forEach((inv) => {
      const data = formatDate(inv.date) || "";
      const equipe = inv.team || "";
      const planilhaId = inv.id || "";
      if (Array.isArray(inv.services)) {
        inv.services.forEach((s) => {
          if (!s || !s.code) return;
          const qtd = parseFloat(s.quantity?.toString?.() || "0");
          const preco = parseFloat(s.price?.toString?.() || "0");
          const rawFactorStr = s.factor?.toString?.();
          const parsedFactor = parseFloat(rawFactorStr || "");
          const fator = !isNaN(parsedFactor) && parsedFactor > 0 ? parsedFactor : factorDefault;
          const und = (s as any).unid || (s as any).und || "";
          const servico = s.activity || "";
          const totalParcial = qtd * preco * fator;
          items.push({
            data,
            equipe,
            planilhaId,
            codigo: s.code,
            servico,
            qtd,
            preco,
            und,
            fator,
            fatorDisplay: rawFactorStr ?? String(factorDefault),
            totalParcial,
          });
        });
      }
    });
    return items;
  }, [invoices]);

  const filteredAndSorted = useMemo(() => {
    const filtered = lineItems.filter((l) => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        l.codigo.toLowerCase().includes(q) ||
        l.servico.toLowerCase().includes(q) ||
        l.equipe.toLowerCase().includes(q) ||
        l.planilhaId.toLowerCase().includes(q)
      );
    });

    return filtered.sort((a, b) => {
      let av: string | number;
      let bv: string | number;
      switch (sortBy) {
        case "codigo":
          av = a.codigo; bv = b.codigo; break;
        case "equipe":
          av = a.equipe; bv = b.equipe; break;
        case "total":
          av = a.totalParcial; bv = b.totalParcial; break;
        case "data":
        default:
          av = a.data; bv = b.data; break;
      }
      if (typeof av === "string" && typeof bv === "string") {
        return sortOrder === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortOrder === "asc" ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [lineItems, searchTerm, sortBy, sortOrder]);

  const reportSummary = useMemo(() => {
    const totalLinhas = filteredAndSorted.length;
    const totalQtd = filteredAndSorted.reduce((sum, l) => sum + l.qtd, 0);
    const totalValor = filteredAndSorted.reduce((sum, l) => sum + l.totalParcial, 0);
    return { totalLinhas, totalQtd, totalValor };
  }, [filteredAndSorted]);

  const handleSort = (col: "data" | "codigo" | "equipe" | "total") => {
    if (sortBy === col) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortOrder("asc"); }
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <i className="fas fa-sort"></i>;
    return sortOrder === "asc" ? <i className="fas fa-sort-up"></i> : <i className="fas fa-sort-down"></i>;
  };

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams((prev) => prev.includes(teamId) ? prev.filter((t) => t !== teamId) : [...prev, teamId]);
  };
  const handleSelectAllTeams = () => setSelectedTeams(teams.map((t) => t.id));
  const handleDeselectAllTeams = () => setSelectedTeams([]);

  const exportToCSV = () => {
    const headers = [
      "Data",
      "Equipe",
      "N Planilha (ID)",
      "Código",
      "Serviço",
      "QTD.",
      "Preço",
      "UND",
      "Fator",
      "Total Parcial",
    ].join(";");

    const csv = filteredAndSorted.map((l) => [
      l.data,
      `"${l.equipe}"`,
      `"${l.planilhaId}"`,
      l.codigo,
      `"${l.servico}"`,
      l.qtd.toFixed(2),
      l.preco.toFixed(2),
      `"${l.und}"`,
      l.fatorDisplay,
      l.totalParcial.toFixed(2),
    ].join(";")).join("\n");

    const blob = new Blob([[headers, csv].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `faturamento-real-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="faturamento-real-report-container">
      <div className="report-header">
        <div className="header-content">
          <h1>Faturamento Real</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="report-content">
        <div className="filters-section">
          <div className="filter-group">
            <div className="teams-header">
              <h3>Equipes</h3>
              <div className="team-controls">
                <button onClick={handleSelectAllTeams} className="control-btn">Selecionar Todas</button>
                <button onClick={handleDeselectAllTeams} className="control-btn">Desmarcar Todas</button>
              </div>
            </div>
            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Período</h3>
            <div className="date-inputs">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                className="date-input"
              />
              <span>até</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                className="date-input"
              />
            </div>
          </div>

          <div className="filter-group">
            <h3>Filtros</h3>
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Pesquisar código, serviço, equipe, ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="action-buttons">
            <button onClick={loadReportData} disabled={loading || selectedTeams.length === 0} className="load-btn">
              {loading ? (<><i className="fas fa-spinner fa-spin"></i>Carregando...</>) : (<><i className="fas fa-search"></i>Gerar Relatório</>)}
            </button>

            {lineItems.length > 0 && (
              <button onClick={exportToCSV} className="export-btn">
                <i className="fas fa-download"></i>
                Exportar CSV
              </button>
            )}
          </div>
        </div>

        {lineItems.length > 0 && (
          <div className="summary-section">
            <h3>Resumo</h3>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalLinhas}</div>
                <div className="card-label">Linhas</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalQtd.toFixed(2)}</div>
                <div className="card-label">Quantidade Total</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(reportSummary.totalValor)}</div>
                <div className="card-label">Valor Total</div>
              </div>
            </div>
          </div>
        )}

        {lineItems.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Resultados ({filteredAndSorted.length} linhas)</h3>
            </div>
            <div className="table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("data")} className="sortable-header">Data {getSortIcon("data")}</th>
                    <th onClick={() => handleSort("equipe")} className="sortable-header">Equipe {getSortIcon("equipe")}</th>
                    <th>N Planilha (ID)</th>
                    <th onClick={() => handleSort("codigo")} className="sortable-header">Código {getSortIcon("codigo")}</th>
                    <th>Serviço</th>
                    <th>QTD.</th>
                    <th>Preço</th>
                    <th>UND</th>
                    <th>Fator</th>
                    <th onClick={() => handleSort("total")} className="sortable-header">Total Parcial {getSortIcon("total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSorted.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.data}</td>
                      <td>{l.equipe}</td>
                      <td>{l.planilhaId}</td>
                      <td>{l.codigo}</td>
                      <td>{l.servico}</td>
                      <td>{l.qtd.toFixed(2)}</td>
                      <td>{formatCurrency(l.preco)}</td>
                      <td>{l.und}</td>
                      <td>{l.fatorDisplay}</td>
                      <td>{formatCurrency(l.totalParcial)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {lineItems.length === 0 && !loading && (
          <div className="no-data">
            <i className="fas fa-file-invoice-dollar"></i>
            <h3>Nenhum dado carregado</h3>
            <p>Selecione equipes e período, depois clique em "Gerar Relatório".</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaturamentoRealReport;
